import { colord } from 'colord';
import clonedeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import { WidgetId } from 'types/idTypes';
import { InfographState } from 'types/infographTypes';
import { Gradient, GradientType } from 'types/pageTypes';
import { WidgetType } from 'types/widget.types';
import { randomPick, shuffle } from 'utils/array';
import { contrast, getLuminance, isColorBlack, isColorWhite, isNeutral, toHex } from 'utils/color';
import { GET_WIDGET_COLOR_FN_MAP } from 'widgets/Widget.config';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { AllWidgetData, GroupWidgetData, WidgetMap } from 'widgets/Widget.types';
import { getWidgetRect } from 'widgets/WidgetLayout.helpers';

import {
  BaseLayoutWidgets,
  ColorMap,
  DecorativeLayoutWidgets,
  FullyCoveredLayoutWidgets,
  InitialPageColors,
  InitialWidgetColors,
  LayoutMap,
  PartiallyCoveredLayoutWidgets,
  Theme,
} from './colorShuffle.types';

const WHITE_HEX = '#ffffff';
const BLACK_HEX = '#000000';
const LIGHT_LUMINANCE_LEVEL = 0.98;
const MIN_DATA_COLOR_CONTRAST = 1.5;
const TEXT_WHITE_LUMINANCE_LEVEL = 0.19;

/**
 * Shuffles the theme colors and makes sure that its the same as the previous theme array.
 *
 * @param {Theme} theme - The theme array to shuffle.
 * @param {Theme} previousTheme - The previous theme array.
 * @param {number} seed - Number to use when we randomize the values.
 * @returns {Theme} - The shuffled theme array.
 */
export const shuffleThemeColors = (theme: Theme, previousTheme: Theme, seed?: number) => {
  if (theme.length === 1) return theme;

  // we don't want to shuffle the same colors in the same order multiple times
  // if they are the same just reverse the order
  const newTheme = shuffle(clonedeep(theme), seed);
  if (isEqual(newTheme, previousTheme)) return newTheme.reverse();

  return newTheme;
};

/**
 * Collapses an array of colors to a color map based on a given theme.
 * If there are more colors than theme colors, the colors will be collapsed to the theme colors.
 * If there are more theme colors than colors, each color will be mapped to a unique theme color.
 *
 * @param theme - The theme to map the colors to.
 * @param colors - The array of colors to collapse.
 * @param colorMap - The color map to populate.
 * @returns The populated color map.
 */
export function collapseColors(
  theme: Theme,
  colors: string[],
  colorMap: ColorMap,
  shouldKeepExistingColorMap: boolean,
) {
  // Count occurrences of each color
  const colorCount: { [key: string]: number } = {};
  colors.forEach((color) => {
    if (colorCount[color]) colorCount[color]++;
    else colorCount[color] = 1;
  });

  // sort the colors by how often they are used
  const sortedColors = Object.keys(colorCount).sort((a, b) => colorCount[b] - colorCount[a]);

  // Check if all are unique and should each map to a different theme color
  const uniqueColors = [...new Set(colors)];

  // min luminance for keeping light colors as light

  // We have enough theme colors: Map each color to its corresponding theme color directly
  if (uniqueColors.length <= theme.length) {
    sortedColors.forEach((color, index) => {
      const shouldSkip = shouldKeepExistingColorMap && colorMap[color];
      if (!shouldSkip) {
        if (getLuminance(color) > LIGHT_LUMINANCE_LEVEL) colorMap[color] = color;
        else colorMap[color] = theme[index];
      }
    });
    // Not enough theme colors -> collapse colors to theme colors
  } else {
    let themeIndex = 0;
    sortedColors.forEach((color) => {
      // if the color is light keep it as light
      if (getLuminance(color) > LIGHT_LUMINANCE_LEVEL) {
        colorMap[color] = color;
        // This color appears more than once and should get a unique theme color
      } else if (colorCount[color] > 1) {
        colorMap[color] = theme[themeIndex % theme.length];
        themeIndex++;
      } else {
        // If all colors are unique but not enough theme colors, use the first theme color for all
        // Or use the first theme color for single occurrences
        colorMap[color] = theme[0];
      }
    });
  }

  return colorMap;
}

/**
 * Generates data colors:
 * 1. different colors before will be different colors after applying the theme
 * 2. If there's not enough theme colors it will use the extra theme colors
 *
 * @param initialDataColorArr - The initial array of data colors.
 * @param theme - The main theme colors.
 * @param extraThemeColors - The additional theme colors.
 * @param colorMap - The color map for assigning colors or using existing assignments.
 * @param backgroundColor - The background color.
 * @param preAssignedColors - data colors that have been assigned before and should be kept
 *
 * @returns The new data colors array.
 */
export const generateDataColors = (
  initialDataColorArr: string[],
  theme: Theme,
  extraThemeColors: Theme,
  colorMap: ColorMap,
  backgroundColor: string,
  preAssignedColors: ColorMap = {},
) => {
  // Map to store the first chosen color for each unique initial color
  const colorAssignment: ColorMap = { ...preAssignedColors };

  // 1. Assign colors from the colorMap (no duplicates unless the colors are the same before)
  for (const initialColor of initialDataColorArr) {
    const hasExistingColor = colorMap[initialColor] && colorMap[initialColor] !== backgroundColor;
    if (!hasExistingColor) continue;

    // Skip if the color has been assigned and we don't want it to be the same
    const newColor = colorMap[initialColor];
    const colorHasBeenAssigned = Object.values(colorAssignment).includes(newColor);

    if (colorHasBeenAssigned) {
      // if the color that has been assigned is not the same as the initial color, skip it
      const otherInitialColor = Object.keys(colorAssignment).find((key) => colorAssignment[key] === newColor);
      if (otherInitialColor !== initialColor) continue;
    }

    // Get color from colorMap if available and not the background color
    colorAssignment[initialColor] = colorMap[initialColor];
  }

  // 2. Assign colors from the theme (or extra theme colors) for not yet assigned colors
  initialDataColorArr.forEach((color) => {
    if (!colorAssignment[color]) {
      const colorAssignmentValues = Object.values(colorAssignment);

      // Exclude background color, current color, and already assigned colors
      const themeColorFiltered = theme.filter(
        (c) =>
          c !== backgroundColor &&
          c !== color &&
          !colorAssignmentValues.includes(c) &&
          contrast(backgroundColor, c) > MIN_DATA_COLOR_CONTRAST,
      );
      const extraThemeFiltered = extraThemeColors.filter(
        (c) =>
          c !== backgroundColor &&
          c !== color &&
          !colorAssignmentValues.includes(c) &&
          contrast(backgroundColor, c) > MIN_DATA_COLOR_CONTRAST,
      );

      let newColor = '';
      // if possible choose a color from the theme
      if (themeColorFiltered.length) newColor = randomPickThemeColor(themeColorFiltered, colorMap);
      // else choose a color from the extra theme colors
      if (extraThemeFiltered.length && !newColor) newColor = randomPickThemeColor(extraThemeFiltered, colorMap);
      // just in case theres lots and lots of data colors choose a random color from the extra theme colors
      if (!newColor) newColor = randomPickThemeColor(extraThemeColors, colorMap);

      colorAssignment[color] = newColor;
      if (!colorMap[color]) colorMap[color] = newColor;
    }
  });

  // Build the new data colors array based on the assignments
  const newDataColors = initialDataColorArr.map((color) => colorAssignment[color]);

  return { newDataColors, dataColorMap: colorAssignment };
};

/**
 * Populates the initial page colors object with the initial colors of the pages.
 *
 * @param infograph - The infograph state object.
 * @param initialPageColors - The object containing the initial colors for each page.
 */
export const setInitialPageColors = (infograph: InfographState, initialPageColors: InitialPageColors) => {
  // iterate over the pages and set the initial colors
  for (const pageId in infograph.pages) {
    if (initialPageColors[pageId]) continue;

    const page = infograph.pages[pageId];
    initialPageColors[pageId] = page.gradientBackground ? 'gradient' : toHex(page.background);
  }
};

/**
 * Populates the initial widget colors object with the initial widget colors of the pages.
 *
 * @param widgets - All the widgets in the infograph.
 * @param widgetInitialColors - The object containing the initial widget colors
 */
export const setInitialWidgetColors = (widgets: WidgetMap, widgetInitialColors: InitialWidgetColors) => {
  for (const widgetId in widgets) {
    if (widgetInitialColors[widgetId]) continue;

    const widgetType = getWidgetTypeFromId(widgetId);
    const getWidgetColor = GET_WIDGET_COLOR_FN_MAP[widgetType];
    if (!getWidgetColor) continue;

    const widget = widgets[widgetId] as AllWidgetData;
    const widgetColor = getWidgetColor(widget);
    widgetInitialColors[widgetId] = Array.isArray(widgetColor) ? widgetColor.map(toHex) : toHex(widgetColor);
  }
};

/**
 * Generates the page background color
 *
 * @param theme - The array of theme colors.
 * @param initialColor - The initial color.
 * @param currentColor - The current color.
 * @param backgroundLuminanceTreshhold - The threshold for determining if a color is light or dark.
 * @param pageColorMap - The map of assigned colors for each page.
 * @returns The background color.
 */
export const getBackgroundColor = (
  theme: Theme,
  initialColor: string,
  currentColor: string,
  backgroundLuminanceTreshhold: number,
  pageColorMap: ColorMap,
  seed?: number,
) => {
  const isInitiallyGradient = initialColor === 'gradient';
  const isInitiallyLight = getLuminance(initialColor) > backgroundLuminanceTreshhold;

  // if initially light - keep it as light
  if (isInitiallyLight && !isInitiallyGradient) return initialColor;

  // if this page color has been assigned a color - use that
  const pageMapColor = pageColorMap[currentColor];
  if (pageMapColor) return pageMapColor;

  // if the page color is not in the theme colors use the first one for the first page
  const initialIndex = theme.indexOf(currentColor);
  const isFirstPageColorAssignment = Object.keys(pageColorMap).length === 0;

  if (initialIndex === -1 && isFirstPageColorAssignment) return theme[0];
  if (initialIndex === -1) return randomPickThemeColor(theme, pageColorMap, seed);

  // if the page color is in the theme colors use the next one
  const nextIndex = initialIndex + 1;
  return theme[nextIndex] || theme[0];
};

/**
 * Picks a random theme color from the provided theme colors, excluding those already used in the color map (if possible).
 *
 * @param themeColors - The array of theme colors to choose from.
 * @param colorMap - The map of colors already used.
 * @returns A randomly picked theme color.
 */
export const randomPickThemeColor = (themeColors: Theme, colorMap: ColorMap, seed?: number) => {
  // if there is a theme color that hasn't been used in the colorMap, use it
  const unusedThemeColors = themeColors.filter((color) => !Object.values(colorMap).includes(color));
  if (unusedThemeColors.length) return randomPick(unusedThemeColors, seed);

  // else just pick a random color from the theme colors
  return randomPick(themeColors);
};

/**
 * Calculates the contrast text color based on the background color, initial color and suggested new Color
 *
 * If the initial color is white or black, it is kept if it has enough contrast.
 * If a new color is provided and has enough contrast with the background, it is used.
 * Otherwise, the fallback to the text color (white or black depending on which has the highest contrast)
 *
 * @param backgroundColor The background color.
 * @param initialColor The initial color.
 * @param newColor The new color.
 * @param minContrast The minimum contrast ratio required.
 * @returns The contrast text color.
 */
export const getContrastTextColor = (
  backgroundColor: string,
  initialColor: string,
  newColor: string,
  minContrast: number,
) => {
  const whiteColorContrast = contrast(WHITE_HEX, backgroundColor);
  const blackColorContrast = contrast(BLACK_HEX, backgroundColor);

  // if the initial color has enough contrast with the background keep it
  if (initialColor) {
    // if the initial color is white or black - keep it if it has enough contrast
    const isInitiallyBlack = isColorBlack(initialColor);
    const isInitiallyWhite = isColorWhite(initialColor);

    if (isInitiallyBlack || isInitiallyWhite) {
      if (!backgroundColor) return initialColor;

      const contrastRatio = contrast(initialColor, backgroundColor);
      if (contrastRatio >= minContrast) return initialColor;

      // else return white or black
      if (isInitiallyWhite && blackColorContrast >= minContrast) {
        return BLACK_HEX;
      }
      if (isInitiallyBlack && whiteColorContrast >= minContrast) {
        return WHITE_HEX;
      }
      // its not initially black or white
      // check if we can use a color from the colorMap
    } else {
      const isInitiallyWhite = getLuminance(initialColor) >= TEXT_WHITE_LUMINANCE_LEVEL;
      if (newColor) {
        // If there's no background color - use the initial text color and its contrast
        if (!backgroundColor) {
          // if its initially white, we want to use black as the background (else vice versa)
          const newBackground = isInitiallyWhite ? BLACK_HEX : WHITE_HEX;
          const initialContrast = contrast(initialColor, newBackground);
          const newContrast = contrast(newColor, newBackground);

          // if the new color has enough contrast with the background color, use it
          if (newContrast >= initialContrast) return newColor;
          // else return white or black
          return isInitiallyWhite ? WHITE_HEX : BLACK_HEX;
        }

        const contrastRatio = contrast(newColor, backgroundColor);
        if (contrastRatio >= minContrast) {
          return newColor;
        }
      }

      // fallback to black or white
      if (!backgroundColor) return isInitiallyWhite ? WHITE_HEX : BLACK_HEX;
    }
  }

  // Fallback to: white or black
  const textColor = whiteColorContrast > blackColorContrast ? WHITE_HEX : BLACK_HEX;
  return textColor;
};

/**
 * Retrieves the colors from the base layout widgets and their initial colors - sorted by the most commonly used colors)
 *
 * @param baseLayoutWidgets - The array of base layout widgets.
 * @param widgetInitialColors - The object containing the initial colors of the widgets, with widget IDs as keys.
 *
 * @returns An array of colors, sorted by the most commonly used colors.
 */
export const getBaseLayoutColorsSorted = (
  baseLayoutWidgets: BaseLayoutWidgets,
  widgetInitialColors: InitialWidgetColors,
) => {
  const colors: string[] = [];
  const colorCount: { [key: string]: number } = {};

  for (const baseLayoutWidget of baseLayoutWidgets) {
    const widgetId = baseLayoutWidget.id;
    const initialColor = getInitialColor(widgetInitialColors[widgetId]);
    if (!initialColor) continue;
    // skip if the initial color is an array (data colors)
    if (Array.isArray(initialColor)) continue;

    colorCount[initialColor] = colorCount[initialColor] ? colorCount[initialColor] + 1 : 1;
    colors.push(initialColor);
  }

  // sort by most commonly used colors in colorCount (so we can increase the chance that they are assigned a color from the theme)
  colors.sort((a, b) => {
    // if the colors have the same count, sort by color name
    if (colorCount[b] === colorCount[a]) return a.localeCompare(b);
    return colorCount[b] - colorCount[a]; // Sort by count otherwise
  });

  return colors;
};

/**
 * Turns the layout widgets into a layout map so we can easily look up the values later
 *
 * @param decorativeLayoutWidgets - An array of decorative layout widgets.
 * @param baseLayoutWidgets - An array of base layout widgets.
 * @param fullyCoveredLayoutWidgets - An array of fully covered layout widgets.
 * @param partiallyCoveredLayoutWidgets - An array of partially covered layout widgets.
 * @returns The layout map object.
 */
export const createLayoutMap = (
  decorativeLayoutWidgets: DecorativeLayoutWidgets,
  baseLayoutWidgets: BaseLayoutWidgets,
  fullyCoveredLayoutWidgets: FullyCoveredLayoutWidgets,
  partiallyCoveredLayoutWidgets: PartiallyCoveredLayoutWidgets,
) => {
  const layoutMap: LayoutMap = {};

  decorativeLayoutWidgets.forEach((baseLayoutWidget) => {
    layoutMap[baseLayoutWidget.id] = { type: 'decorative', layerPosition: baseLayoutWidget.layerPosition };
  });

  baseLayoutWidgets.forEach((baseLayoutWidget) => {
    layoutMap[baseLayoutWidget.id] = { type: 'non-intersected', layerPosition: baseLayoutWidget.layerPosition };
  });

  partiallyCoveredLayoutWidgets.forEach((baseLayoutWidget) => {
    if (!layoutMap[baseLayoutWidget.id]) {
      layoutMap[baseLayoutWidget.id] = {
        type: 'partially-intersected',
        intersectIds: baseLayoutWidget.intersectIds,
        layerPosition: baseLayoutWidget.layerPosition,
      };
    }
  });

  fullyCoveredLayoutWidgets.forEach((baseLayoutWidget) => {
    if (!layoutMap[baseLayoutWidget.id]) {
      layoutMap[baseLayoutWidget.id] = {
        type: 'fully-covered',
        wrappedById: baseLayoutWidget.wrappedById,
        layerPosition: baseLayoutWidget.layerPosition,
      };
    }
  });

  return layoutMap;
};

/**
 * Checks if the current color of a widget is the same as the current background color.
 * @param bgColor - The background color to compare against.
 * @param widgetType - The type of the widget.
 * @param widgetId - The ID of the widget.
 * @returns True if the current color of the widget is the same as the background color, false otherwise.
 */
export const isSameAsPageBackground = (bgColor: string, widgetType: WidgetType, widgetData: AllWidgetData) => {
  const getColorFn = GET_WIDGET_COLOR_FN_MAP[widgetType];
  if (!getColorFn) return false;

  const currentColor = GET_WIDGET_COLOR_FN_MAP[widgetType](widgetData);
  if (typeof currentColor !== 'string') return false;

  return toHex(currentColor) === bgColor;
};

/**
 * Retrieves the rectangle dimensions of the parent widget for a given statchart widget.
 * TODO: move this to widget.helpers after rotation is considered
 *
 * @param layerOrder - The order of the widgets in the layer.
 * @param widgets - The map of widgets.
 * @param widgetId - The ID of the statchart widget.
 * @returns The rectangle dimensions of the parent widget.
 */
export const getStatchartParentWidgetRect = (layerOrder: WidgetId[], widgets: WidgetMap, widgetId: WidgetId) => {
  for (let i = 0; i < layerOrder.length; i++) {
    const id = layerOrder[i];
    const widget = widgets[id] as GroupWidgetData;

    if (widget.memberWidgetIds?.length) {
      const hasWidgetAsMember = !!widget.memberWidgetIds.find((memberId) => memberId === widgetId);
      if (hasWidgetAsMember) return getWidgetRect(widget);
    }
  }

  return { topPx: 0, leftPx: 0, widthPx: 0, heightPx: 0 };
};

export const getInitialColor = (initialColor: string | string[]): string => {
  if (Array.isArray(initialColor)) return initialColor[0];
  return initialColor;
};

/**
 * Replaces the colors in a Gradient object with colors from a theme array while
 * maintaining the original gradient's rotation/position and structure.
 *
 * @param gradient - Gradient object with type, colors, and positioning properties
 * @param themeColors - Array of hex color strings to use as replacements
 * @returns A new Gradient object with replaced colors
 */
export const replaceGradientColors = (gradient: Gradient, themeColors: string[]): Gradient => {
  const newGradient: Gradient = {
    type: gradient.type,
    colors: [],
  };

  // Copy the positioning properties
  if (gradient.type === GradientType.LinearGradient && gradient.angle !== undefined) {
    newGradient.angle = gradient.angle;
  } else if (gradient.type === GradientType.RadialGradient && gradient.circleAtCenter) {
    newGradient.circleAtCenter = gradient.circleAtCenter;
  }

  // Extract original colors and their alpha values if applicable
  const originalColors = gradient.colors.map((color) => {
    const alphaMatch = color.match(/rgba\s*\([^,]+,[^,]+,[^,]+,\s*([^)]+)\)/);
    const alpha = alphaMatch ? parseFloat(alphaMatch[1]) : null;
    return { color, alpha };
  });

  // Ensure we have enough theme colors (cycle through if needed)
  const cycledThemeColors = [...themeColors];
  while (cycledThemeColors.length < originalColors.length) {
    // Generate additional colors that maintain contrast with existing colors
    const lastColor = colord(cycledThemeColors[cycledThemeColors.length - 1]);

    // if theres not enough theme colors - create a new color
    // that's shifted in hue but maintains similar brightness/saturation
    const newColor = lastColor.rotate(30).toHex();

    cycledThemeColors.push(newColor);
  }

  // Replace original colors with theme colors while preserving alpha values
  newGradient.colors = originalColors.map((item, index) => {
    const newColor = cycledThemeColors[index % cycledThemeColors.length];

    // If the original was rgba, convert the hex to rgba with the same alpha
    if (item.alpha !== null) {
      return colord(newColor).alpha(item.alpha).toRgbString();
    }

    return newColor;
  });

  return newGradient;
};

/**
 * Checks if the initial border color should be skipped.s
 * skip neutra/black color
 *
 * @param initialBorderColor - The initial border color.
 * @param backgroundColor - The background color.
 * @returns True if the initial border color should be skipped, false otherwise.
 */
export const shouldSkipBorderColor = (initialBorderColor: string, backgroundColor: string): boolean => {
  if (isNeutral(initialBorderColor) && initialBorderColor !== backgroundColor) return true;
  if (getLuminance(initialBorderColor) >= LIGHT_LUMINANCE_LEVEL && initialBorderColor !== backgroundColor) return true;
  if (isColorBlack(initialBorderColor) && initialBorderColor !== backgroundColor) return true;

  return false;
};
